import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";
import getApp from "@/lib/firebase";
import { useAuthContext } from "@/contexts/AuthContext";
import { useHouseholdContext } from "@/contexts/HouseholdContext";
import { useTranslations } from "@/contexts/I18nContext";
import { userFacingMessage } from "@/lib/errors/userFacingError";
import { fetchDocument } from "@/lib/firestore";
import type { Household } from "@/lib/models";

const PENDING_INVITE_KEY = "pending_invite_code";

/**
 * Deep-link landing page for `/join?code=…` (or legacy `/join?token=…`).
 * Routes the user to the right surface per the four sub-flows in
 * [[specs/new-user-onboarding-and-join-flow]]:
 *
 *   A. Signed-out → stash code in localStorage, send to /login
 *   B. Signed in, zero households → stash code, send to /onboarding?join=1
 *   C. Signed in, ≥1 household → call resolveInvite directly; on success,
 *      navigate to /dashboard and show a passive "You joined X" toast
 */
function JoinRouter() {
  const navigate = useNavigate();
  const t = useTranslations();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuthContext();
  const { profile, loading: householdLoading } = useHouseholdContext();
  const resolvedRef = useRef(false);

  const [status, setStatus] = useState<"resolving" | "success" | "error">(
    "resolving"
  );
  const [message, setMessage] = useState<string>(t("join.checking"));
  const [householdLabel, setHouseholdLabel] = useState<string | null>(null);

  // `code` is our canonical param name; accept `token` as an alias for
  // direct-from-URL compatibility with how the Cloud Function is invoked.
  const rawCode = searchParams.get("code") ?? searchParams.get("token");

  useEffect(() => {
    if (authLoading) return;
    if (resolvedRef.current) return;

    const code = rawCode?.trim() ?? "";
    if (!code) {
      setStatus("error");
      setMessage(t("join.errors.missingCode"));
      resolvedRef.current = true;
      return;
    }

    // Sub-flow B: signed out → stash and go to /login.
    if (!user) {
      window.localStorage.setItem(PENDING_INVITE_KEY, code);
      navigate("/login", { replace: true });
      resolvedRef.current = true;
      return;
    }

    // Wait for profile to load before deciding between C and D.
    if (householdLoading) return;

    // Sub-flow C: signed in, zero households → stash code so the gate
    // can auto-open the Join sheet pre-filled with it.
    if (!profile?.households?.length) {
      window.localStorage.setItem(PENDING_INVITE_KEY, code);
      navigate("/onboarding?join=1", { replace: true });
      resolvedRef.current = true;
      return;
    }

    // Sub-flow D: signed in, ≥1 household → resolve directly, switch to
    // the new household, then show a passive confirmation.
    resolvedRef.current = true;
    (async () => {
      try {
        const functions = getFunctions(getApp());
        const resolveInvite = httpsCallable<
          { token: string },
          {
            success?: boolean;
            alreadyMember?: boolean;
            householdId?: string;
          }
        >(functions, "resolveInvite");
        const result = await resolveInvite({ token: code });
        const data = result.data;

        if (data.alreadyMember) {
          window.localStorage.removeItem(PENDING_INVITE_KEY);
          navigate("/dashboard", { replace: true });
          return;
        }

        if (data.householdId) {
          window.localStorage.setItem(
            "zoey_selected_household",
            data.householdId
          );
          // Best-effort: fetch the label so the confirmation has context.
          try {
            const hh = await fetchDocument<Household>(
              "households",
              data.householdId
            );
            if (hh?.household_label) setHouseholdLabel(hh.household_label);
          } catch {
            // Best-effort only.
          }
        }
        window.localStorage.removeItem(PENDING_INVITE_KEY);
        setStatus("success");
      } catch (err) {
        setStatus("error");
        setMessage(userFacingMessage(err, t("join.errors.resolveFailed")));
      }
    })();
  }, [authLoading, user, householdLoading, profile, rawCode, navigate, t]);

  if (status === "success") {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="w-full max-w-sm bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 text-center">
          <h1 className="text-xl font-semibold mb-2">
            {t("join.success.title")}{" "}
            <span style={{ color: "var(--accent-color, #00C7BE)" }}>
              {householdLabel ?? t("join.success.fallbackName")}
            </span>
          </h1>
          <p className="text-sm text-gray-500 mb-5">
            {t("join.success.body")}
          </p>
          <button
            type="button"
            onClick={() => navigate("/dashboard", { replace: true })}
            className="w-full py-2.5 rounded-lg text-white font-semibold"
            style={{ backgroundColor: "var(--accent-color, #00C7BE)" }}
          >
            {t("join.success.cta")}
          </button>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="w-full max-w-sm bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 text-center">
          <h1 className="text-xl font-semibold mb-2">{t("join.error.title")}</h1>
          <p className="text-sm text-gray-500 mb-5">{message}</p>
          <button
            type="button"
            onClick={() => navigate("/dashboard", { replace: true })}
            className="w-full py-2.5 rounded-lg font-semibold border-2"
            style={{
              color: "var(--accent-color, #00C7BE)",
              borderColor: "var(--accent-color, #00C7BE)",
            }}
          >
            {t("join.error.cta")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div
        className="w-8 h-8 border-3 border-t-transparent rounded-full animate-spin"
        style={{
          borderColor: "var(--accent-color, #00C7BE)",
          borderTopColor: "transparent",
        }}
      />
    </div>
  );
}

export function JoinPage() {
  return <JoinRouter />;
}
